import type { AiFirstItemState, AiFirstScore } from '@developerz/domain';
import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { scoreGroups } from '../lib/ai-first-score';
import { orgHref } from '../lib/org';
import { absolute, ago } from '../lib/time';
import { Panel } from './Panel';
import { RepoScorecardGroup } from './RepoScorecardGroup';
import { StatusPill, type StatusTone } from './StatusPill';
import { buttonClass } from './ui/Button';

// The onboarding scorecard on the repo view (epic #3756) — what the latest
// onboarding run proved about this repo. Presenter: RepoDetail hands it the
// repo row's `ai_first_score` and `ai_first_at`, and renders nothing without a
// scorecard.
//
// THREE STORED FACTS, NONE DERIVED HERE:
//   - the items, grouped by the state the box graded (`lib/ai-first-score.ts`);
//   - the GATE PROOF, which is the `gate_proved` item — the stored record that
//     `bin/setup` then `bin/check` ran green on the box at base and at head. The
//     verdict's own `proof` object rides the `onboarding_verdict` event and is
//     not stored on the repo, so this reads the item that is;
//   - the AI-FIRST STAMP, shown as the date it is. It is NOT a readiness verdict
//     — that is `<RepoReadiness/>`'s, from `GET /v1/next-steps`, and a stamp
//     re-read into "ready" here would be the second implementation that panel
//     replaced. The unstamped line states the rule that stamps it, nothing more.

const TONE: Record<AiFirstItemState, StatusTone> = {
  ok: 'success',
  gap: 'warning',
  refused: 'neutral',
  unprovable: 'danger',
};

export function RepoScorecard(props: {
  readonly score: AiFirstScore;
  readonly aiFirstAt: string | null;
}): JSX.Element {
  const proof = () => props.score.items.gate_proved;
  return (
    <Panel title={t('repo_detail.score_title')} class="repo-scorecard">
      <p class="card__desc">{t('repo_detail.score_desc')}</p>
      <p class="card__desc">
        {t('repo_detail.score_graded', { when: ago(props.score.at) })}
        {' · '}
        <A
          class={buttonClass({ variant: 'link' })}
          href={orgHref(`/fleet/runs/${encodeURIComponent(props.score.run_id)}`)}
        >
          {t('repo_detail.score_run', { id: props.score.run_id })}
        </A>
      </p>
      <div class="repo-scorecard__proof">
        <span class="repo-scorecard__proof-label">{t('repo_detail.score_proof')}</span>
        <StatusPill tone={TONE[proof().state]}>
          {t(`repo_detail.score_state_${proof().state}`)}
        </StatusPill>
        <Show when={proof().evidence[0]}>
          {(line) => <span class="repo-scorecard__evidence">{line()}</span>}
        </Show>
      </div>
      <p class="card__desc">
        <Show when={props.aiFirstAt} fallback={t('repo_detail.score_stamp_none')}>
          {(at) => t('repo_detail.score_stamp', { when: absolute(at()) })}
        </Show>
      </p>
      <For each={scoreGroups(props.score)}>{(group) => <RepoScorecardGroup group={group} />}</For>
    </Panel>
  );
}
